import { Flex, Table } from 'antd'
import { useState } from 'react'

import {
  useTreatmentServiceGetPregnancyMonitoringEndpoint,
  useTreatmentServiceGetPregnancyMonitoringListEndpoint
} from 'src/api/queries'
import { PregnancyMonitoringSortBy, PregnancyType } from 'src/api/requests'
import {
  PageBreadcrumbs,
  Select,
  PersonPregnancyDetailsModal
} from 'src/components'
import {
  usePregnancyConfig,
  usePregnancyColumns
} from 'src/features/persons/components'
import { useTablePagination, useUser, useDefaultSortingItem } from 'src/hooks'
import { useLocales } from 'src/locales'
import {
  normalizeOrdering,
  getOrderingDirection,
  resetPagination
} from 'src/utils'

export const InstitutionPregnancyMonitoring = () => {
  const { t } = useLocales()
  const { userData } = useUser()

  const pagination = useTablePagination()
  const { options } = usePregnancyConfig()

  const [pregnancyId, setPregnancyId] = useState<string | null>(null)
  const { columns } = usePregnancyColumns({
    onOpenDetails: (id) => {
      setPregnancyId(id)
    }
  })

  const [pregnancyTypeId, setPregnancyTypeId] = useState('')
  const [ordering, setOrdering] = useState('')
  const { defaultSortingOption } = useDefaultSortingItem()

  const queryParams = {
    pageNumber: pagination.current,
    pageSize: pagination.pageSize,
    legalEntityId: userData?.currentLegalEntityId,
    ...(ordering && {
      sortBy: normalizeOrdering(ordering) as PregnancyMonitoringSortBy,
      sortDirection: getOrderingDirection(ordering)
    }),
    ...(pregnancyTypeId && { pregnancyType: pregnancyTypeId as PregnancyType })
  }

  const { data, isLoading } =
    useTreatmentServiceGetPregnancyMonitoringListEndpoint(queryParams)

  const onSelect = (val: string) => {
    setOrdering(val)
    resetPagination(pagination)
  }

  const onPregnancySelect = (val: string) => {
    setPregnancyTypeId(val)
    resetPagination(pagination)
  }

  const pregnancyTypeOptions = [
    defaultSortingOption,
    ...Object.values(PregnancyType).map((item) => ({
      value: item,
      label: t(`pregnancy.pregnancy_type_status.${item}`)
    }))
  ]

  return (
    <PageBreadcrumbs
      items={[{ title: t('persons.person_pregnancy') }]}
      title={t('persons.person_pregnancy')}
    >
      <Flex vertical gap={16}>
        <Flex gap={8}>
          <Select
            options={pregnancyTypeOptions}
            placeholder={t('pregnancy.pregnancy_type')}
            onSelect={onPregnancySelect}
          />

          <Select onSelect={onSelect} options={options} />
        </Flex>

        <Table
          rowKey="id"
          scroll={{ x: 'max-content' }}
          loading={isLoading}
          columns={columns}
          dataSource={data?.data}
          pagination={{ ...pagination, total: data?.totalCount }}
        />

        {pregnancyId && (
          <PersonPregnancyDetailsModal
            id={pregnancyId}
            onCancel={() => setPregnancyId(null)}
            useQueryFn={useTreatmentServiceGetPregnancyMonitoringEndpoint}
          />
        )}
      </Flex>
    </PageBreadcrumbs>
  )
}
